import { useRef, useState } from 'react';
import {
  Upload, CheckCircle2, AlertCircle, Loader2, FileDown,
  BookOpen, TrendingUp, TrendingDown,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import {
  useUploadAccountingBalances,
  useAccountingBalanceSummary,
  CHAPTER_LABELS,
  CHAPTER_ORDER,
  type PcecChapter,
} from './accountingBalancesApi';
import { apiPath } from '@/lib/api/client';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(v: string | undefined | null) {
  if (!v) return '—';
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  return new Intl.NumberFormat('fr-MA', { notation: 'compact', maximumFractionDigits: 1 }).format(n);
}

const CHAPTER_COLOUR: Record<PcecChapter, string> = {
  TRESORERIE: 'bg-sky-100 text-sky-800',
  CREDITS:    'bg-blue-100 text-blue-800',
  TITRES:     'bg-indigo-100 text-indigo-800',
  DEPOTS:     'bg-violet-100 text-violet-800',
  DERIVES:    'bg-amber-100 text-amber-800',
  HORS_BILAN: 'bg-rose-100 text-rose-800',
  AUTRES:     'bg-gray-100 text-gray-700',
};

// ── Page ──────────────────────────────────────────────────────────────────────

/**
 * Phase 4.4.ACCT-BAL — Accounting balances ingest page.
 *
 * Allows ALM_ADMIN users to upload a CSV of outstanding principal balances
 * from the core accounting system, keyed by PCEC chapter × currency × date.
 * Shows a chapter-level summary after upload.
 */
export function AccountingBalancesPage() {
  const fileRef = useRef<HTMLInputElement>(null);
  const [file,    setFile]    = useState<File | null>(null);
  const [viewDate, setViewDate] = useState<string>('');

  const upload = useUploadAccountingBalances();

  // After successful upload, default the view date to what was loaded
  const primaryDate = upload.data?.primaryDate ?? viewDate;

  const summary = useAccountingBalanceSummary(
    primaryDate || undefined,
    undefined,
    { enabled: Boolean(primaryDate) },
  );

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    setFile(e.target.files?.[0] ?? null);
    upload.reset();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) return;
    upload.mutate(file);
  }

  function handleReset() {
    setFile(null);
    upload.reset();
    if (fileRef.current) fileRef.current.value = '';
  }

  const templateUrl = apiPath('alm', 'data', 'accounting-balances', 'template');

  return (
    <div className="space-y-6 p-6 max-w-4xl mx-auto">

      {/* ── Header ── */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Accounting Balances</h1>
        <p className="text-sm text-gray-500 mt-1">
          Upload outstanding principal balances (encours) from the accounting system
          by PCEC chapter and currency.  Used for non-maturing product positioning
          and MatrixDimension.PCEC reconciliation.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* ── Upload card ── */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-4 w-4 text-gray-500" />
              Upload CSV
            </CardTitle>
            <CardDescription>
              One row per PCEC code × currency × arrêté date.
              Re-uploading the same date replaces existing rows (idempotent).
            </CardDescription>
          </CardHeader>
          <CardContent>
            {upload.isSuccess ? (
              <div className="space-y-4">
                <div className="flex items-start gap-3 rounded-lg bg-emerald-50 border border-emerald-200 p-4">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold text-emerald-800">Upload successful</p>
                    <p className="text-sm text-emerald-700 mt-1">
                      {upload.data.rowsWritten} rows written · arrêté {upload.data.primaryDate}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {Object.entries(upload.data.rowsByChapter).map(([ch, count]) => (
                        <span
                          key={ch}
                          className={`text-xs px-2 py-0.5 rounded-full font-medium ${CHAPTER_COLOUR[ch as PcecChapter] ?? 'bg-gray-100 text-gray-700'}`}
                        >
                          {CHAPTER_LABELS[ch as PcecChapter] ?? ch}: {count}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={handleReset} className="w-full">
                  Upload another file
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {upload.isError && (
                  <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{(upload.error as any)?.message ?? 'Upload failed.'}</span>
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label htmlFor="acbal-file">CSV file</Label>
                  <Input
                    id="acbal-file"
                    ref={fileRef}
                    type="file"
                    accept=".csv,text/csv"
                    onChange={handleFileChange}
                  />
                  {file && (
                    <p className="text-xs text-gray-500">{file.name} · {(file.size / 1024).toFixed(1)} KB</p>
                  )}
                </div>

                <Button type="submit" disabled={!file || upload.isPending} className="w-full">
                  {upload.isPending
                    ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Uploading…</>
                    : <><Upload className="h-4 w-4 mr-2" />Upload balances</>
                  }
                </Button>
              </form>
            )}

            {/* Template download */}
            <div className="mt-4 pt-4 border-t">
              <a href={templateUrl} download="accounting_balances_template.csv">
                <Button variant="ghost" size="sm" className="w-full text-gray-600">
                  <FileDown className="h-4 w-4 mr-2" />
                  Download CSV template
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>

        {/* ── Format guide ── */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-gray-500" />
              CSV format
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-gray-700">
            <div className="rounded-md bg-gray-50 border p-3 font-mono text-xs leading-relaxed overflow-x-auto">
              as_of_date,pcec_code,pcec_sub_chapter,<br/>
              pcec_chapter,pcec_label,currency,<br/>
              balance,balance_type,bs_position
            </div>
            <ul className="space-y-1.5 text-xs text-gray-600">
              <li><span className="font-semibold">as_of_date</span> — YYYY-MM-DD (arrêté date)</li>
              <li><span className="font-semibold">pcec_code</span> — full PCEC account code (e.g. 2411)</li>
              <li><span className="font-semibold">pcec_sub_chapter</span> — optional intermediate grouping</li>
              <li><span className="font-semibold">pcec_chapter</span> — one of: TRESORERIE · CREDITS · TITRES · DEPOTS · DERIVES · HORS_BILAN · AUTRES</li>
              <li><span className="font-semibold">balance</span> — absolute outstanding principal ≥ 0</li>
              <li><span className="font-semibold">balance_type</span> — ASSET or LIABILITY</li>
              <li><span className="font-semibold">bs_position</span> — BILAN or HORS_BILAN</li>
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* ── Chapter summary ── */}
      {(summary.data && summary.data.length > 0) && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Chapter Summary</CardTitle>
                <CardDescription>Outstanding balances by PCEC chapter · {primaryDate}</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="view-date" className="text-xs text-gray-500 sr-only">Date</Label>
                <Input
                  id="view-date"
                  type="date"
                  value={viewDate || primaryDate}
                  onChange={e => setViewDate(e.target.value)}
                  className="h-8 text-xs w-40"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Chapter</th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">CCY</th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">
                      <span className="flex items-center justify-end gap-1">
                        <TrendingUp className="h-3 w-3 text-emerald-600" /> Asset
                      </span>
                    </th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">
                      <span className="flex items-center justify-end gap-1">
                        <TrendingDown className="h-3 w-3 text-red-500" /> Liability
                      </span>
                    </th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {CHAPTER_ORDER.flatMap(ch =>
                    (summary.data ?? [])
                      .filter(r => r.pcecChapter === ch)
                      .map(r => {
                        const asset = parseFloat(r.totalAsset  || '0');
                        const liab  = parseFloat(r.totalLiability || '0');
                        const net   = asset - liab;
                        return (
                          <tr key={`${r.pcecChapter}-${r.currency}`} className="border-t border-gray-100 hover:bg-gray-50">
                            <td className="px-3 py-2">
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${CHAPTER_COLOUR[r.pcecChapter as PcecChapter] ?? 'bg-gray-100 text-gray-700'}`}>
                                {CHAPTER_LABELS[r.pcecChapter as PcecChapter] ?? r.pcecChapter}
                              </span>
                            </td>
                            <td className="px-3 py-2 font-mono text-xs text-gray-600">{r.currency}</td>
                            <td className="px-3 py-2 text-right font-mono text-emerald-700">{fmt(r.totalAsset)}</td>
                            <td className="px-3 py-2 text-right font-mono text-red-600">{fmt(r.totalLiability)}</td>
                            <td className={`px-3 py-2 text-right font-mono font-semibold ${net >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                              {fmt(String(net))}
                            </td>
                          </tr>
                        );
                      })
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {summary.isLoading && (
        <div className="flex items-center justify-center py-8 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Loading balances…
        </div>
      )}

    </div>
  );
}
